import { useRef, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { IAlert } from 'common/interfaces/IAlert'
import {
  useCreateSeriesTemplateMutation,
  useUpdateSeriesTemplateMutation,
} from 'features/Series/api/series.api'
import { toSeriesApiError } from 'features/Series/api/series.transform'
import type { SeriesApiError, SeriesTemplate } from 'features/Series/api/series.types'
import { SERIES_PAGES_TEXT } from 'features/Series/pages/shared/seriesPagesText'

import {
  saveAsNewName,
  templateFieldErrors,
  toWriteBody,
  type SeriesTemplateDraft,
  type TemplateField,
} from '../templateForm.model'

const TOASTS = SERIES_PAGES_TEXT.toasts
const NOTICE_TITLE = 'Series templates'
const EMR_UNAVAILABLE = 'emr_unavailable'
const NOT_FOUND = 404

export type TemplateSaveErrors = {
  fields: Partial<Record<TemplateField, string>>
  other: string[]
}

type Args = { siteId: string; template: SeriesTemplate | null; onDone: () => void }

type TemplateSave = {
  save: (draft: SeriesTemplateDraft) => void
  saveAsNew: (draft: SeriesTemplateDraft) => void
  isSaving: boolean
  errors: TemplateSaveErrors
}

const NO_ERRORS: TemplateSaveErrors = { fields: {}, other: [] }

const errorNotice = (description: string): IAlert => ({
  title: NOTICE_TITLE,
  type: ALERT_CONSTANTS.ERROR,
  description,
})

// A refusal with no field key (e.g. OpenEMR rejects the code) falls back to its message.
const refusalErrors = (failure: SeriesApiError): TemplateSaveErrors => {
  const mapped = templateFieldErrors(failure.errors)
  const empty = Object.keys(mapped.fields).length === 0 && mapped.other.length === 0
  return empty ? { fields: {}, other: [failure.message] } : mapped
}

export const useTemplateSave = ({ siteId, template, onDone }: Args): TemplateSave => {
  const [createTemplate] = useCreateSeriesTemplateMutation()
  const [updateTemplate] = useUpdateSeriesTemplateMutation()
  const { setNotification } = useNotification()
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<TemplateSaveErrors>(NO_ERRORS)
  const isSendingRef = useRef(false)

  const run = async (send: () => Promise<unknown>) => {
    if (isSendingRef.current) return
    isSendingRef.current = true
    setIsSaving(true)
    setErrors(NO_ERRORS)
    let shouldClose = false
    try {
      await send()
      shouldClose = true
    } catch (error) {
      const failure = toSeriesApiError(error)
      if (failure.status === NOT_FOUND) {
        setNotification(errorNotice(TOASTS.templateGone))
        shouldClose = true
      } else if (failure.code === EMR_UNAVAILABLE) {
        setNotification(errorNotice(TOASTS.templateEmrNoAnswer))
      } else {
        setErrors(refusalErrors(failure))
      }
    } finally {
      isSendingRef.current = false
      setIsSaving(false)
    }
    if (shouldClose) onDone()
  }

  const create = (draft: SeriesTemplateDraft, name?: string) =>
    run(() => {
      const body = toWriteBody(draft)
      return createTemplate({ siteId, data: name ? { ...body, name } : body }).unwrap()
    })

  const save = (draft: SeriesTemplateDraft) => {
    if (!template) {
      void create(draft)
      return
    }
    void run(() =>
      updateTemplate({ siteId, templateId: template.id, data: toWriteBody(draft) }).unwrap(),
    )
  }

  const saveAsNew = (draft: SeriesTemplateDraft) => {
    void create(draft, template ? saveAsNewName(draft.name, template.name) : undefined)
  }

  return { save, saveAsNew, isSaving, errors }
}
